import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  Paperclip,
  Pencil,
  Plus,
  Search,
  Trash2,
  GitCompareArrows,
  FileUp,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { api, API, eur, formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";
import NuovaLavorazioneButton from "@/components/NuovaLavorazioneButton";
import { printPdfBlobDirect } from "@/lib/directPrint";
import { openAttachmentViewer } from "@/lib/attachmentViewer";

const oggi = () => new Date().toISOString().slice(0, 10);

const vuoto = () => ({
  data: oggi(),
  numero_documento: "",
  cantiere_id: null,
  lavorazione_id: null,
  materiale_descrizione: "",
  fornitore: "",
  fornitore_piva: "",
  azienda_id: null,
  azienda_nome: "",
  imponibile: "",
  iva_percentuale: "22",
  iva_importo_effettivo: null,
  totale_effettivo: null,
  iva_mista: false,
  note: "",
  allegato_path: null,
  allegato_nome: null,
  ripartizione_attiva: false,
  ripartizioni: [],
  magazzino: false,
  attrezzatura: false,
  attrezzatura_nome: "",
});

const dataIt = (valore) =>
  valore
    ? new Date(`${valore}T12:00:00`).toLocaleDateString("it-IT")
    : "-";

const fatturaRealmenteRipartita = (item) =>
  Boolean(item?.ripartizione_attiva && Array.isArray(item?.ripartizioni) && item.ripartizioni.length >= 2);

const pulisciNotaRipartizione = (valore) =>
  String(valore || "")
    .replace(/fattura\s+ripartita/gi, "")
    .split(/\r?\n/)
    .map((riga) => riga.trim())
    .filter(Boolean)
    .join("\n");

const notaRegistroVisualizzata = (item) => {
  const note = pulisciNotaRipartizione(item?.note);
  return [
    ...(note ? [note] : []),
    ...(fatturaRealmenteRipartita(item) ? ["Fattura Ripartita"] : []),
  ].join("\n");
};

const descrizioneDestinazioni = (ripartizioni, descrizioneCorrente) => {
  const magazzino = (ripartizioni || []).some((quota) => Boolean(quota.magazzino));
  const attrezzatura = (ripartizioni || []).some((quota) => Boolean(quota.attrezzatura));
  if (magazzino && attrezzatura) return "Magazzino / Attrezzatura";
  if (attrezzatura) return "Attrezzatura";
  if (magazzino) return "Magazzino";
  return /^\s*(?:magazzino|attrezzatura|magazzino\s*\/\s*attrezzatura)\s*$/i.test(descrizioneCorrente || "") ? "" : descrizioneCorrente;
};

export default function RegistroCantieriPage() {
  const navigate = useNavigate();
  const annoCorrente = new Date().getFullYear();

  const [items, setItems] = useState(null);
  const [cantieri, setCantieri] = useState([]);
  const [aziende, setAziende] = useState([]);
  const [fornitoriDettaglio, setFornitoriDettaglio] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(vuoto());
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [importingAruba, setImportingAruba] = useState(false);
  const arubaInputRef = useRef(null);
  const codaPdfRef = useRef([]);
  const totalePdfRef = useRef(0);
  const completatiPdfRef = useRef(0);
  const [pdfDaImportazione, setPdfDaImportazione] = useState(false);
  const [progressoImportazione, setProgressoImportazione] = useState(null);
  const [fornitoreOpen, setFornitoreOpen] = useState(false);
  const [savingFornitore, setSavingFornitore] = useState(false);
  const [importazioneInAttesa, setImportazioneInAttesa] = useState(null);
  const [lavorazioniFattura, setLavorazioniFattura] = useState([]);
  const [lavorazioneFatturaCreataId, setLavorazioneFatturaCreataId] = useState(null);
  const [lavorazioniQuote, setLavorazioniQuote] = useState({});
  const [lavorazioneQuotaCreata, setLavorazioneQuotaCreata] = useState(null);
  const [nuovoFornitore, setNuovoFornitore] = useState({
    nome: "", piva: "", codice_fiscale: "", email: "", telefono: "", indirizzo: "", note: "",
  });

  const [confrontoOpen, setConfrontoOpen] = useState(false);
  const [confrontoFornitore, setConfrontoFornitore] = useState("");
  const [confrontoLoading, setConfrontoLoading] = useState(false);
  const [confrontoRisultato, setConfrontoRisultato] = useState(null);
  const [confrontoPeriodo, setConfrontoPeriodo] = useState("tutto");
  const [confrontoMese, setConfrontoMese] = useState("");
  const [confrontoAnno, setConfrontoAnno] = useState(
    String(new Date().getFullYear())
  );

  const [query, setQuery] = useState("");
  const [fornitoreFilter, setFornitoreFilter] = useState("Tutti");
  const [anno, setAnno] = useState(String(annoCorrente));
  const [giorno, setGiorno] = useState("");
  const [mese, setMese] = useState("");

  const [config, setConfig] = useState({
    iva_aliquote: [0, 4, 10, 22],
    fornitori: [],
  });

  useEffect(() => {
    if (!form.cantiere_id) {
      setLavorazioniFattura([]);
      return;
    }

    let attivo = true;
    api.get(`/cantieri/${form.cantiere_id}/lavorazioni`)
      .then((response) => {
        if (attivo) {
          setLavorazioniFattura(
            Array.isArray(response.data) ? response.data : []
          );
        }
      })
      .catch(() => {
        if (attivo) setLavorazioniFattura([]);
      });

    return () => {
      attivo = false;
    };
  }, [form.cantiere_id]);

  useEffect(() => {
    if (
      !lavorazioneFatturaCreataId ||
      !lavorazioniFattura.some(
        (lavorazione) => lavorazione.id === lavorazioneFatturaCreataId
      )
    ) return;

    // La nuova voce va selezionata quando è già presente tra le opzioni del
    // menu. Radix altrimenti può mantenere il placeholder del valore precedente.
    setForm((corrente) =>
      corrente.lavorazione_id === lavorazioneFatturaCreataId
        ? corrente
        : { ...corrente, lavorazione_id: lavorazioneFatturaCreataId }
    );
    setLavorazioneFatturaCreataId(null);
  }, [lavorazioneFatturaCreataId, lavorazioniFattura]);

  useEffect(() => {
    const selezione = lavorazioneQuotaCreata;
    if (
      !selezione ||
      !(lavorazioniQuote[selezione.cantiereId] || []).some(
        (lavorazione) => lavorazione.id === selezione.id
      )
    ) return;

    setForm((corrente) => ({
      ...corrente,
      ripartizioni: corrente.ripartizioni.map((quota, indice) =>
        indice === selezione.indice
          ? { ...quota, lavorazione_id: selezione.id }
          : quota
      ),
    }));
    setLavorazioneQuotaCreata(null);
  }, [lavorazioneQuotaCreata, lavorazioniQuote]);

  const load = () =>
    api
      .get("/registro-cantieri")
      .then((response) =>
        setItems(Array.isArray(response.data) ? response.data : [])
      )
      .catch(() => setItems([]));

  const loadConfig = () =>
    api
      .get("/registro-cantieri-config")
      .then((response) =>
        setConfig({
          iva_aliquote: Array.isArray(response.data?.iva_aliquote)
            ? response.data.iva_aliquote
            : [0, 4, 10, 22],
          fornitori: Array.isArray(response.data?.fornitori)
            ? response.data.fornitori
            : [],
        })
      )
      .catch(() => {});

  useEffect(() => {
    load();
    loadConfig();

    api
      .get("/gestionale/anagrafiche/fornitori")
      .then((response) => {
        const elenco = Array.isArray(response.data)
          ? response.data
          : [];
        setFornitoriDettaglio(elenco.filter((item) => !item.archived_at));

        const nomi = elenco
          .filter((item) => !item.archived_at)
          .map((item) => String(item.nome || "").trim())
          .filter(Boolean);

        setConfig((precedente) => ({
          ...precedente,
          fornitori: Array.from(
            new Set([
              ...(Array.isArray(precedente.fornitori)
                ? precedente.fornitori
                : []),
              ...nomi,
            ])
          ).sort((a, b) =>
            a.localeCompare(b, "it", {
              sensitivity: "base",
            })
          ),
        }));
      })
      .catch(() => {});

    Promise.all([api.get("/cantieri"), api.get("/collaborazioni")])
      .then(([cantieriResponse, collaborazioniResponse]) => {
        const cantieriAttivi = (Array.isArray(cantieriResponse.data) ? cantieriResponse.data : [])
          .filter((item) => item.stato !== "Completato")
          .map((item) => ({ ...item, tipo_destinazione: "cantiere" }));
        const collaborazioni = (Array.isArray(collaborazioniResponse.data) ? collaborazioniResponse.data : [])
          .map((item) => ({ ...item, id: `collab:${item.id}`, tipo_destinazione: "collaborazione" }));
        setCantieri([...cantieriAttivi, ...collaborazioni]);
      })
      .catch(() => setCantieri([]));

    api
      .get("/gestionale/anagrafiche/aziende")
      .then((response) =>
        setAziende(
          (Array.isArray(response.data) ? response.data : [])
            .filter((item) => !item.archived_at)
            .sort((a, b) =>
              String(a.nome || "").localeCompare(
                String(b.nome || ""),
                "it",
                { sensitivity: "base" }
              )
            )
        )
      )
      .catch(() => setAziende([]));
  }, []);

  const normalizzaFornitore = (valore) =>
    String(valore || "")
      .trim()
      .toLocaleLowerCase("it-IT");

  const apriConfronto = () => {
    setConfrontoFornitore("");
    setConfrontoPeriodo("tutto");
    setConfrontoMese("");
    setConfrontoAnno(String(new Date().getFullYear()));
    setConfrontoRisultato(null);
    setConfrontoOpen(true);
  };

  const dataNelPeriodoConfronto = (data) => {
    const valore = String(data || "").slice(0, 10);

    if (!valore) return false;

    if (confrontoPeriodo === "mese") {
      return (
        !!confrontoMese &&
        valore.slice(0, 7) === confrontoMese
      );
    }

    if (confrontoPeriodo === "anno") {
      return (
        !!confrontoAnno &&
        valore.slice(0, 4) === String(confrontoAnno)
      );
    }

    return true;
  };

  const etichettaPeriodoConfronto = () => {
    if (confrontoPeriodo === "mese" && confrontoMese) {
      const [anno, mese] = confrontoMese.split("-");

      return new Date(
        Number(anno),
        Number(mese) - 1,
        1
      ).toLocaleDateString("it-IT", {
        month: "long",
        year: "numeric",
      });
    }

    if (confrontoPeriodo === "anno" && confrontoAnno) {
      return `Anno ${confrontoAnno}`;
    }

    return "Tutto il periodo";
  };
  const confrontaFornitore = async () => {
    if (!confrontoFornitore) {
      toast.error("Seleziona un fornitore");
      return;
    }

    setConfrontoLoading(true);
    setConfrontoRisultato(null);

    try {
      const nomeNormalizzato =
        normalizzaFornitore(confrontoFornitore);

      const fattureFornitore = (items || []).filter(
        (item) =>
          normalizzaFornitore(item.fornitore) ===
            nomeNormalizzato &&
          dataNelPeriodoConfronto(item.data)
      );

      const rispostaDdt = await api.get("/ddt", {
        params: {
          fornitore: confrontoFornitore,
        },
      });

      const tuttiDdt = Array.isArray(rispostaDdt.data)
        ? rispostaDdt.data
        : [];

      const ddtFornitore = tuttiDdt.filter(
        (item) =>
          normalizzaFornitore(item.fornitore) ===
            nomeNormalizzato &&
          dataNelPeriodoConfronto(item.data_acquisto)
      );

      const totaleFatture = fattureFornitore.reduce(
        (somma, item) =>
          somma + (Number(item.imponibile) || 0),
        0
      );

      const totaleDdt = ddtFornitore.reduce(
        (somma, item) =>
          somma + (Number(item.costo_totale) || 0),
        0
      );

      const differenza = totaleFatture - totaleDdt;

      let stato = "fatture_superiori";

      if (Math.abs(differenza) < 0.005) {
        stato = "coincidente";
      } else if (differenza < 0) {
        stato = "ddt_superiori";
      }

      setConfrontoRisultato({
        fornitore: confrontoFornitore,
        periodo: etichettaPeriodoConfronto(),
        numeroFatture: fattureFornitore.length,
        numeroDdt: ddtFornitore.length,
        totaleFatture,
        totaleDdt,
        differenza,
        stato,
      });
    } catch (errore) {
      toast.error(
        formatApiError(errore.response?.data?.detail) ||
          "Errore durante il confronto"
      );
    } finally {
      setConfrontoLoading(false);
    }
  };
  const exportPdfFatture = async () => {
    setPdfLoading(true);

    try {
      const paramsPdf = anno === "tutti" ? {} : { anno };

      if (fornitoreFilter !== "Tutti") {
        paramsPdf.fornitore = fornitoreFilter;
      }

      if (giorno) {
        paramsPdf.giorno = giorno;
      }

      if (mese) {
        paramsPdf.mese = mese;
      }

      if (query.trim()) {
        paramsPdf.ricerca = query.trim();
      }

      const response = await api.get(
        "/registro-cantieri/export/pdf",
        {
          params: paramsPdf,
          responseType: "blob",
        }
      );

      const blob = new Blob(
        [response.data],
        { type: "application/pdf" }
      );

      printPdfBlobDirect(blob);
    } catch (errore) {
      toast.error(
        formatApiError(errore.response?.data?.detail) ||
          "Errore durante la creazione del PDF"
      );
    } finally {
      setPdfLoading(false);
    }
  };
  const anniDisponibili = useMemo(() => {
    const anniFatture = (items || [])
      .map((item) => Number(String(item.data || "").slice(0, 4)))
      .filter((valore) => Number.isInteger(valore) && valore > 1900);
    const primoAnno = anniFatture.length ? Math.min(annoCorrente, ...anniFatture) : annoCorrente;
    const ultimoAnno = anniFatture.length ? Math.max(annoCorrente, ...anniFatture) : annoCorrente;
    return Array.from({ length: ultimoAnno - primoAnno + 1 }, (_, indice) => ultimoAnno - indice);
  }, [items, annoCorrente]);

  const filtered = useMemo(
    () =>
      (items || []).filter((item) => {
        const testo = [
          item.numero_documento,
          item.materiale_descrizione,
          item.fornitore,
          item.azienda_nome,
          item.note,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return (
          (!query.trim() ||
            testo.includes(query.trim().toLowerCase())) &&
          (anno === "tutti" || String(item.data || "").slice(0, 4) === anno) &&
          (fornitoreFilter === "Tutti" ||
            item.fornitore === fornitoreFilter) &&
          (!giorno || item.data === giorno) &&
          (!mese || (item.data || "").slice(0, 7) === mese)
        );
      }),
    [items, query, fornitoreFilter, anno, giorno, mese]
  );

  const totals = useMemo(
    () =>
      filtered.reduce(
        (totali, item) => ({
          imponibile:
            totali.imponibile + (Number(item.imponibile) || 0),
          iva:
            totali.iva + (Number(item.iva_importo) || 0),
          totale:
            totali.totale + (Number(item.totale) || 0),
        }),
        {
          imponibile: 0,
          iva: 0,
          totale: 0,
        }
      ),
    [filtered]
  );

  const ivaImporto = form.iva_importo_effettivo !== null
    ? Number(form.iva_importo_effettivo) || 0
    : (Number(form.imponibile) || 0) * (Number(form.iva_percentuale) || 0) / 100;

  const totale = form.totale_effettivo !== null
    ? Number(form.totale_effettivo) || 0
    : (Number(form.imponibile) || 0) + ivaImporto;
  const numeroQuota = (valore) => Number(String(valore ?? "").replace(",", ".")) || 0;
  const totaleRipartito = (form.ripartizioni || []).reduce(
    (somma, quota) => somma + numeroQuota(quota.importo),
    0
  );
  const differenzaRipartizione =
    Math.round(((Number(form.imponibile) || 0) - totaleRipartito) * 100) / 100;
  const ripartizioneCorretta =
    form.ripartizione_attiva &&
    form.ripartizioni.length >= 2 &&
    form.ripartizioni.every(
      (quota) =>
        (Boolean(quota.magazzino) || Boolean(quota.attrezzatura) || Boolean(String(quota.cantiere_id || "").trim())) &&
        (
          Math.abs(numeroQuota(quota.importo)) < 0.005 ||
          Math.sign(numeroQuota(quota.importo)) === Math.sign(numeroQuota(form.imponibile))
        )
    ) &&
    Math.abs(differenzaRipartizione) < 0.01;

  const set = (chiave, valore) =>
    setForm((corrente) => ({
      ...corrente,
      [chiave]: valore,
    }));

  const nuovo = () => {
    setEditing(null);
    setLavorazioneFatturaCreataId(null);
    setForm(vuoto());
    setFile(null);
    setPdfDaImportazione(false);
    setLavorazioniQuote({});
    setOpen(true);
  };

  const modifica = (item) => {
    setEditing(item);
    setLavorazioneFatturaCreataId(null);
    const quoteSalvate = Array.isArray(item.ripartizioni)
      ? item.ripartizioni.map((quota) => ({
          cantiere_id: quota.target_id || (quota.collaborazione_id ? `collab:${quota.collaborazione_id}` : quota.cantiere_id) || "",
          lavorazione_id: quota.lavorazione_id || null,
          importo: String(quota.importo ?? ""),
          magazzino: Boolean(quota.magazzino),
          attrezzatura: Boolean(quota.attrezzatura),
          attrezzatura_nome: quota.attrezzatura_nome || "",
        }))
      : [];
    const ripartita = fatturaRealmenteRipartita(item);
    const noteCorrette = pulisciNotaRipartizione(item.note);
    setForm({
      ...vuoto(),
      ...item,
      cantiere_id: item.target_id || (item.collaborazione_id ? `collab:${item.collaborazione_id}` : item.cantiere_id),
      ripartizione_attiva: ripartita,
      ripartizioni: quoteSalvate,
      imponibile: String(item.imponibile ?? ""),
      iva_percentuale: String(item.iva_percentuale ?? 22),
      note: noteCorrette,
      magazzino: Boolean(item.magazzino),
      attrezzatura: Boolean(item.attrezzatura),
      attrezzatura_nome: item.attrezzatura_nome || "",
    });
    setFile(null);
    setPdfDaImportazione(false);
    quoteSalvate.forEach((quota) => caricaLavorazioniQuota(quota.cantiere_id));
    setOpen(true);
  };

  const caricaLavorazioniQuota = async (cantiereId) => {
    if (!cantiereId || lavorazioniQuote[cantiereId]) return;
    try {
      const response = await api.get(`/cantieri/${cantiereId}/lavorazioni`);
      setLavorazioniQuote((corrente) => ({
        ...corrente,
        [cantiereId]: Array.isArray(response.data) ? response.data : [],
      }));
    } catch {
      setLavorazioniQuote((corrente) => ({ ...corrente, [cantiereId]: [] }));
    }
  };

  const nuovaLavorazioneFattura = (nuova) => {
    if (!nuova?.id) return;
    setLavorazioneFatturaCreataId(nuova.id);
    setLavorazioniFattura((correnti) => [
      ...correnti.filter((lavorazione) => lavorazione.id !== nuova.id),
      nuova,
    ]);
  };

  const nuovaLavorazioneQuota = (indice, cantiereId, nuova) => {
    if (!nuova?.id) return;
    setLavorazioniQuote((correnti) => ({
      ...correnti,
      [cantiereId]: [
        ...(correnti[cantiereId] || []).filter(
          (lavorazione) => lavorazione.id !== nuova.id
        ),
        nuova,
      ],
    }));
    setLavorazioneQuotaCreata({ indice, cantiereId, id: nuova.id });
  };

  const aggiungiQuota = () => {
    setForm((corrente) => ({
      ...corrente,
      ripartizioni: [
        ...(corrente.ripartizioni || []),
        { cantiere_id: "", lavorazione_id: null, importo: "0", magazzino: false, attrezzatura: false, attrezzatura_nome: "" },
      ],
    }));
  };

  const aggiornaQuota = (indice, dati) => {
    setForm((corrente) => ({
      ...corrente,
      ripartizioni: corrente.ripartizioni.map((quota, posizione) =>
        posizione === indice ? { ...quota, ...dati } : quota
      ),
    }));
  };

  const aggiornaImportoQuota = (indice, valore) => {
    setForm((corrente) => {
      const quote = corrente.ripartizioni.map((quota, posizione) =>
        posizione === indice ? { ...quota, importo: valore } : { ...quota }
      );
      if (indice < quote.length - 1) {
        const totalePrecedente = quote
          .slice(0, indice + 1)
          .reduce((somma, quota) => somma + (Number(quota.importo) || 0), 0);
        const residuo = Math.round(((Number(corrente.imponibile) || 0) - totalePrecedente) * 100) / 100;
        quote[indice + 1].importo = String(residuo);
        for (let posizione = indice + 2; posizione < quote.length; posizione += 1) {
          quote[posizione].importo = "";
        }
      }
      return { ...corrente, ripartizioni: quote };
    });
  };

  const eliminaQuota = (indice) => {
    setForm((corrente) => ({
      ...corrente,
      ripartizioni: corrente.ripartizioni.filter((_, posizione) => posizione !== indice),
    }));
  };

  const aggiornaProgressoPdf = () => {
    const totale = totalePdfRef.current;
    const completati = completatiPdfRef.current;
    setProgressoImportazione(
      totale > 1 ? { completati, totale } : null
    );
  };

  const continuaCodaPdf = () => {
    const prossimo = codaPdfRef.current.shift();
    if (prossimo) {
      aggiornaProgressoPdf();
      setTimeout(() => analizzaPdf(prossimo), 0);
      return;
    }

    const totale = totalePdfRef.current;
    const completati = completatiPdfRef.current;
    setProgressoImportazione(null);
    totalePdfRef.current = 0;
    completatiPdfRef.current = 0;
    if (totale > 1) {
      toast.success(`Importazione completata: ${completati} fatture salvate su ${totale} PDF`);
    }
  };

  const analizzaPdf = async (pdf) => {
    setImportingAruba(true);
    aggiornaProgressoPdf();
    try {
      const dati = new FormData();
      dati.append("file", pdf);
      const response = await api.post("/registro-cantieri/importa-pdf-aruba", dati, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const estratti = response.data || {};
      if (!estratti.fornitore_censito) {
        setImportazioneInAttesa({ estratti, pdf });
        setNuovoFornitore({
          nome: estratti.fornitore_da_censire?.nome || "",
          piva: estratti.fornitore_da_censire?.piva || "",
          codice_fiscale: estratti.fornitore_da_censire?.codice_fiscale || "",
          email: estratti.fornitore_da_censire?.email || "",
          telefono: estratti.fornitore_da_censire?.telefono || "",
          indirizzo: estratti.fornitore_da_censire?.indirizzo || "",
          note: estratti.fornitore_da_censire?.note || "Importato da fattura PDF",
        });
        setFornitoreOpen(true);
        toast.warning("Fornitore non in anagrafica: verifica i dati e premi Salva fornitore");
        return;
      }
      completaImportazione(estratti, pdf);
    } catch (errore) {
      toast.error(`${pdf.name}: ${formatApiError(errore.response?.data?.detail || errore)}`);
      continuaCodaPdf();
    } finally {
      setImportingAruba(false);
    }
  };

  const importaPdfAruba = async (event) => {
    const selezionati = Array.from(event.target.files || []);
    event.target.value = "";
    if (selezionati.length === 0) return;

    const pdfValidi = selezionati.filter(
      (pdf) => pdf.type === "application/pdf" || pdf.name.toLowerCase().endsWith(".pdf")
    );
    if (pdfValidi.length !== selezionati.length) {
      toast.warning("I file che non sono PDF sono stati ignorati");
    }
    if (pdfValidi.length === 0) {
      toast.error("Seleziona almeno un file PDF");
      return;
    }

    codaPdfRef.current = pdfValidi.slice(1);
    totalePdfRef.current = pdfValidi.length;
    completatiPdfRef.current = 0;
    aggiornaProgressoPdf();
    await analizzaPdf(pdfValidi[0]);
  };

  const completaImportazione = (estratti, pdf, nomeFornitore = null) => {
      setEditing(null);
      setForm({
        ...vuoto(),
        data: estratti.data || oggi(),
        numero_documento: estratti.numero_documento || "",
        cantiere_id: null,
        lavorazione_id: null,
        materiale_descrizione: estratti.materiale_descrizione || "",
        fornitore: nomeFornitore || estratti.fornitore || "",
        azienda_id: estratti.azienda_id || null,
        azienda_nome: estratti.azienda_nome || "",
        imponibile: String(estratti.imponibile ?? ""),
        iva_percentuale: String(estratti.iva_percentuale ?? 22),
        iva_importo_effettivo: estratti.iva_importo_effettivo ?? estratti.iva_importo ?? null,
        totale_effettivo: estratti.totale_effettivo ?? estratti.totale ?? null,
        iva_mista: Boolean(estratti.iva_mista),
        note: estratti.note || "",
      });
      setFile(pdf);
      setPdfDaImportazione(true);
      setOpen(true);
      toast.success(
        totalePdfRef.current > 1
          ? `PDF ${completatiPdfRef.current + 1} di ${totalePdfRef.current}: verifica e salva`
          : "Dati della fattura importati. Verifica e salva."
      );
  };

  const salvaFornitoreImportato = async (event) => {
    event.preventDefault();
    if (!nuovoFornitore.nome.trim() || !nuovoFornitore.piva.trim()) {
      toast.error("Nome e partita IVA sono obbligatori");
      return;
    }
    setSavingFornitore(true);
    try {
      await api.post("/gestionale/anagrafiche/fornitori", nuovoFornitore);
      const nome = nuovoFornitore.nome.trim();
      setConfig((precedente) => ({
        ...precedente,
        fornitori: Array.from(new Set([...(precedente.fornitori || []), nome])).sort((a, b) => a.localeCompare(b, "it")),
      }));
      setFornitoreOpen(false);
      completaImportazione(importazioneInAttesa.estratti, importazioneInAttesa.pdf, nome);
      setImportazioneInAttesa(null);
      toast.success("Fornitore censito correttamente");
    } catch (errore) {
      toast.error(formatApiError(errore.response?.data?.detail || errore));
    } finally {
      setSavingFornitore(false);
    }
  };

  const salva = async (event) => {
    event.preventDefault();

    if (!form.numero_documento.trim()) {
      toast.error("Inserisci il numero della fattura");
      return;
    }

    if (!form.fornitore.trim()) {
      toast.error("Seleziona il fornitore");
      return;
    }

    if (!String(form.azienda_nome || "").trim()) {
      toast.error("Seleziona l'azienda");
      return;
    }

    if (form.ripartizione_attiva && !ripartizioneCorretta) {
      toast.error(
        `La somma delle quote deve coincidere con l'imponibile. Differenza: ${eur(differenzaRipartizione)}`
      );
      return;
    }

    setSaving(true);

    try {
      let allegato_path = form.allegato_path;
      let allegato_nome = form.allegato_nome;

      if (file) {
        const datiFile = new FormData();
        datiFile.append("file", file);

        const upload = await api.post(
          "/uploads/bolla",
          datiFile,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        allegato_path = upload.data.path;
        allegato_nome = upload.data.nome;
      }

      const payload = {
        ...form,
        note: pulisciNotaRipartizione(form.note),
        cantiere_id: form.ripartizione_attiva || form.magazzino || form.attrezzatura ? null : (form.cantiere_id || null),
        lavorazione_id: !form.ripartizione_attiva && !form.magazzino && !form.attrezzatura && form.cantiere_id
          ? (form.lavorazione_id || null)
          : null,
        ripartizioni: form.ripartizione_attiva
          ? form.ripartizioni.map((quota) => ({
              cantiere_id: quota.cantiere_id,
              lavorazione_id: quota.lavorazione_id || null,
              importo: Number(quota.importo) || 0,
              magazzino: Boolean(quota.magazzino),
              attrezzatura: Boolean(quota.attrezzatura),
              attrezzatura_nome: quota.attrezzatura_nome || "",
            }))
          : [],
        imponibile: Number(form.imponibile) || 0,
        iva_percentuale: Number(form.iva_percentuale) || 0,
        iva_importo_effettivo: form.iva_importo_effettivo === null ? null : Number(form.iva_importo_effettivo),
        totale_effettivo: form.totale_effettivo === null ? null : Number(form.totale_effettivo),
        iva_mista: Boolean(form.iva_mista),
        allegato_path,
        allegato_nome,
      };

      if (editing) {
        await api.put(
          `/registro-cantieri/${editing.id}`,
          payload
        );
      } else {
        await api.post("/registro-cantieri", payload);
      }

      toast.success(
        editing ? "Fattura aggiornata" : "Fattura registrata"
      );

      setOpen(false);
      load();
      if (!editing && pdfDaImportazione) {
        completatiPdfRef.current += 1;
        setPdfDaImportazione(false);
        continuaCodaPdf();
      }
    } catch (errore) {
      toast.error(
        formatApiError(errore.response?.data?.detail) ||
          "Errore durante il salvataggio"
      );
    } finally {
      setSaving(false);
    }
  };

  const elimina = async (item) => {
    if (
      !window.confirm(
        `Eliminare la fattura ${item.numero_documento}?`
      )
    ) {
      return;
    }

    try {
      await api.delete(`/registro-cantieri/${item.id}`);

      setItems((corrente) =>
        corrente.filter((voce) => voce.id !== item.id)
      );

      toast.success("Fattura eliminata");
    } catch {
      toast.error("Impossibile eliminare la fattura");
    }
  };

  return (
    <AppShell>
      <div className="h-[calc(100vh-7rem)] flex flex-col min-h-0">
        <button
          type="button"
          onClick={() => navigate("/gestionale")}
          className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary mb-5 self-start"
        >
          <ArrowLeft className="h-4 w-4" />
          Gestionale
        </button>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-5">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">
              Gestionale
            </p>

            <div className="mt-1 flex flex-wrap items-center gap-3">
              <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-bold">
                Registro fatture
              </h1>
              <Select value={anno} onValueChange={(valore) => { setAnno(valore); setGiorno(""); setMese(""); }}>
                <SelectTrigger className="w-[130px] font-semibold"><SelectValue placeholder="Anno" /></SelectTrigger>
                <SelectContent><SelectItem value="tutti">Tutti gli anni</SelectItem>{anniDisponibili.map((valore) => <SelectItem key={valore} value={String(valore)}>Anno {valore}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <p className="text-muted-foreground mt-2">
              Fatture dei fornitori, importi IVA e relativi allegati.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <input
              ref={arubaInputRef}
              type="file"
              accept="application/pdf,.pdf"
              multiple
              className="hidden"
              onChange={importaPdfAruba}
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => arubaInputRef.current?.click()}
              disabled={importingAruba}
              className="font-bold h-11 px-5"
            >
              {importingAruba ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileUp className="h-4 w-4 mr-2" />}
              {importingAruba
                ? progressoImportazione
                  ? `Lettura PDF ${progressoImportazione.completati + 1}/${progressoImportazione.totale}...`
                  : "Lettura PDF..."
                : "Importa PDF"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={exportPdfFatture}
              disabled={pdfLoading}
              className="font-bold h-11 px-5"
            >
              <Download className="h-4 w-4 mr-2" />
              {pdfLoading ? "Creazione PDF..." : "Esporta PDF"}
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={apriConfronto}
              className="font-bold h-11 px-5"
            >
              <GitCompareArrows className="h-4 w-4 mr-2" />
              Confronta fornitore
            </Button>

            <Button
              onClick={nuovo}
              className="font-bold h-11 px-6"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nuova fattura
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          {[
            ["IMPONIBILE", totals.imponibile],
            ["IVA", totals.iva],
            ["TOTALE FATTURE", totals.totale],
          ].map(([etichetta, valore]) => (
            <div
              key={etichetta}
              className="rounded-xl border bg-card p-5"
            >
              <p className="text-xs tracking-[0.16em] font-bold text-muted-foreground">
                {etichetta}
              </p>

              <p className="font-heading text-2xl font-bold mt-2">
                {eur(valore)}
              </p>
            </div>
          ))}
        </div>

        <div className="rounded-xl border bg-card p-4 mb-4 grid grid-cols-1 md:grid-cols-5 gap-3 flex-none">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />

            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Numero fattura, fornitore, descrizione o note..."
              className="pl-9"
            />
          </div>

          <SearchableCombobox value={fornitoreFilter} onValueChange={setFornitoreFilter}
            options={[{value:"Tutti",label:"Tutti i fornitori"},...config.fornitori.map(fornitore=>({value:fornitore,label:fornitore}))]}
            placeholder="Tutti i fornitori" searchPlaceholder="Scrivi il fornitore..." />

          <Input
            type="date"
            min={`${anno}-01-01`}
            max={`${anno}-12-31`}
            value={giorno}
            onChange={(event) => setGiorno(event.target.value)}
            title="Filtra per giorno"
          />

          <div className="flex gap-2">
            <Input
              type="month"
              min={`${anno}-01`}
              max={`${anno}-12`}
              value={mese}
              onChange={(event) => setMese(event.target.value)}
              title="Filtra per mese"
            />

            <Button
              variant="outline"
              onClick={() => {
                setQuery("");
                setFornitoreFilter("Tutti");
                setGiorno("");
                setMese("");
              }}
            >
              Azzera
            </Button>
          </div>
        </div>

        <div className="rounded-xl border bg-card flex-1 min-h-[260px] overflow-auto overscroll-contain">
          <table className="w-full min-w-[1250px] text-sm">
            <thead className="sticky top-0 z-10 bg-muted shadow-sm">
              <tr>
                {[
                  "DATA",
                  "NUMERO FATTURA",
                  "FORNITORE",
                  "AZIENDA",
                  "CANTIERE",
                  "DESCRIZIONE",
                  "IMPONIBILE",
                  "IVA",
                  "TOTALE",
                  "ALLEGATO",
                  "NOTE",
                  "",
                ].map((titolo) => (
                  <th
                    key={titolo}
                    className={`text-left py-3 tracking-wider text-muted-foreground ${
                      titolo === "DATA" || titolo === "NUMERO FATTURA"
                        ? "w-[96px] px-2 text-[10px]"
                        : "px-4 text-xs"
                    }`}
                  >
                    {titolo}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {items === null ? (
                <tr>
                  <td
                    colSpan="12"
                    className="p-8 text-center"
                  >
                    Caricamento...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan="12"
                    className="p-8 text-center text-muted-foreground"
                  >
                    Nessuna fattura registrata
                  </td>
                </tr>
              ) : (
                filtered.map((item) => (
                  <tr
                    key={item.id}
                    className="border-t hover:bg-muted/30"
                  >
                    <td className="w-[96px] px-2 py-2.5 text-xs whitespace-nowrap">
                      {dataIt(item.data)}
                    </td>

                    <td className="w-[96px] px-2 py-2.5 text-xs font-semibold">
                      {item.numero_documento}
                    </td>

                    <td className="px-4 py-3">
                      {item.fornitore || "-"}
                    </td>

                    <td className="px-4 py-3">
                      {item.azienda_nome || "-"}
                    </td>

                    <td className="px-4 py-3">
                      {item.cantiere_nome || "-"}
                    </td>

                    <td className="px-4 py-3 max-w-[260px] whitespace-normal break-words">
                      {item.materiale_descrizione || "-"}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      {eur(item.imponibile)}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      {eur(item.iva_importo)} ({item.iva_percentuale}%)
                    </td>

                    <td className="px-4 py-3 font-bold whitespace-nowrap">
                      {eur(item.totale)}
                    </td>

                    <td className="px-4 py-3">
                      {item.allegato_path ? (
                        <button
                          type="button"
                          className="text-primary inline-flex items-center gap-1"
                          onClick={() => openAttachmentViewer(`${API}/uploads/bolla/${item.allegato_path}`, item.allegato_nome || `Fattura ${item.numero_documento}`)}
                        >
                          <Paperclip className="h-4 w-4" />
                          Vedi
                        </button>
                      ) : (
                        "-"
                      )}
                    </td>

                    <td className="px-4 py-3 max-w-[240px] whitespace-normal break-words">
                      {notaRegistroVisualizzata(item) || "-"}
                    </td>

                    <td className="px-4 py-3 whitespace-nowrap">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => modifica(item)}
                        title="Modifica fattura"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>

                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => elimina(item)}
                        title="Elimina fattura"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog
        open={confrontoOpen}
        onOpenChange={setConfrontoOpen}
      >
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Confronta fornitore</DialogTitle>

            <DialogDescription>
              Confronta l'imponibile delle fatture con il totale
              dei DDT registrati per lo stesso fornitore.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div>
              <Label>Fornitore *</Label>

              <SearchableCombobox
                value={confrontoFornitore}
                onValueChange={(valore) => {
                  setConfrontoFornitore(valore);
                  setConfrontoRisultato(null);
                }}
                options={config.fornitori.map(fornitore=>({value:fornitore,label:fornitore}))}
                placeholder="Seleziona il fornitore" searchPlaceholder="Scrivi il fornitore..." />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Periodo</Label>

                <Select
                  value={confrontoPeriodo}
                  onValueChange={(valore) => {
                    setConfrontoPeriodo(valore);
                    setConfrontoRisultato(null);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>

                  <SelectContent>
                    <SelectItem value="tutto">
                      Tutto il periodo
                    </SelectItem>

                    <SelectItem value="mese">
                      Seleziona mese
                    </SelectItem>

                    <SelectItem value="anno">
                      Seleziona anno
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {confrontoPeriodo === "mese" && (
                <div>
                  <Label>Mese *</Label>

                  <Input
                    type="month"
                    value={confrontoMese}
                    onChange={(event) => {
                      setConfrontoMese(event.target.value);
                      setConfrontoRisultato(null);
                    }}
                  />
                </div>
              )}

              {confrontoPeriodo === "anno" && (
                <div>
                  <Label>Anno *</Label>

                  <Input
                    type="number"
                    min="2000"
                    max="2100"
                    step="1"
                    value={confrontoAnno}
                    onChange={(event) => {
                      setConfrontoAnno(event.target.value);
                      setConfrontoRisultato(null);
                    }}
                  />
                </div>
              )}
            </div>
            {confrontoRisultato && (
              <div className="rounded-xl border bg-card overflow-hidden">
                <div className="p-5 border-b bg-muted/40">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">
                        Risultato confronto
                      </p>

                      <p className="font-heading text-xl font-bold mt-1">
                        {confrontoRisultato.fornitore}
                      </p>

                      <p className="text-sm text-muted-foreground mt-1 capitalize">
                        {confrontoRisultato.periodo}
                      </p>
                    </div>

                    <span
                      className={`h-4 w-4 rounded-full shrink-0 ${
                        confrontoRisultato.stato === "coincidente"
                          ? "bg-emerald-500"
                          : confrontoRisultato.stato === "ddt_superiori"
                            ? "bg-red-500"
                            : "bg-orange-500"
                      }`}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-px bg-border">
                  <div className="bg-card p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Fatture
                    </p>

                    <p className="font-heading text-xl font-bold mt-1">
                      {eur(confrontoRisultato.totaleFatture)}
                    </p>

                    <p className="text-xs text-muted-foreground mt-1">
                      {confrontoRisultato.numeroFatture} documenti
                    </p>
                  </div>

                  <div className="bg-card p-4">
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      DDT
                    </p>

                    <p className="font-heading text-xl font-bold mt-1">
                      {eur(confrontoRisultato.totaleDdt)}
                    </p>

                    <p className="text-xs text-muted-foreground mt-1">
                      {confrontoRisultato.numeroDdt} documenti
                    </p>
                  </div>
                </div>

                <div className="p-5 flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Differenza fatture - DDT
                    </p>

                    <p
                      className={`font-heading text-2xl font-bold mt-1 ${
                        confrontoRisultato.stato === "coincidente"
                          ? "text-emerald-600"
                          : confrontoRisultato.stato === "ddt_superiori"
                            ? "text-red-600"
                            : "text-orange-600"
                      }`}
                    >
                      {eur(confrontoRisultato.differenza)}
                    </p>
                  </div>

                  <p
                    className={`text-sm font-semibold text-right ${
                      confrontoRisultato.stato === "coincidente"
                        ? "text-emerald-700"
                        : confrontoRisultato.stato === "ddt_superiori"
                          ? "text-red-700"
                          : "text-orange-700"
                    }`}
                  >
                    {confrontoRisultato.stato === "coincidente"
                      ? "Fatture e DDT coincidono"
                      : confrontoRisultato.stato === "ddt_superiori"
                        ? "DDT superiori alle fatture"
                        : "Fatture superiori ai DDT"}
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfrontoOpen(false)}
            >
              Chiudi
            </Button>

            <Button
              type="button"
              onClick={confrontaFornitore}
              disabled={
                !confrontoFornitore ||
                confrontoLoading ||
                (confrontoPeriodo === "mese" &&
                  !confrontoMese) ||
                (confrontoPeriodo === "anno" &&
                  !confrontoAnno)
              }
            >
              {confrontoLoading
                ? "Confronto..."
                : "Esegui confronto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editing ? "Modifica fattura" : "Nuova fattura"}
            </DialogTitle>
          </DialogHeader>

          <form
            onSubmit={salva}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Data fattura *</Label>
                <Input
                  type="date"
                  value={form.data}
                  onChange={(event) =>
                    set("data", event.target.value)
                  }
                  required
                />
              </div>

              <div>
                <Label>Numero fattura *</Label>
                <Input
                  value={form.numero_documento}
                  onChange={(event) =>
                    set("numero_documento", event.target.value)
                  }
                  required
                  placeholder="Es. FT-2026/001"
                />
              </div>

              <div>
                <Label>Imponibile (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.imponibile}
                  onChange={(event) =>
                    setForm((corrente) => ({...corrente, imponibile: event.target.value, iva_importo_effettivo: null, totale_effettivo: null, iva_mista: false}))
                  }
                  required
                />
              </div>

              <div>
                <Label>IVA (%)</Label>

                <Select
                  value={String(form.iva_percentuale)}
                  onValueChange={(valore) => setForm((corrente) => ({...corrente, iva_percentuale: valore, iva_importo_effettivo: null, totale_effettivo: null, iva_mista: false}))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona IVA" />
                  </SelectTrigger>

                  <SelectContent>
                    {!config.iva_aliquote
                      .map(String)
                      .includes(String(form.iva_percentuale)) && (
                      <SelectItem
                        value={String(form.iva_percentuale)}
                      >
                        {form.iva_mista ? "IVA mista" : `${form.iva_percentuale}%`}
                      </SelectItem>
                    )}

                    {config.iva_aliquote.map((aliquota) => (
                      <SelectItem
                        key={aliquota}
                        value={String(aliquota)}
                      >
                        {aliquota}%
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="sm:col-span-2">
                <Label>Fornitore *</Label>

                <SearchableCombobox
                  value={form.fornitore || undefined}
                  onValueChange={(valore) => {
                    const anagrafica = fornitoriDettaglio.find((item) => String(item.nome || "").trim().toLocaleLowerCase("it") === String(valore || "").trim().toLocaleLowerCase("it"));
                    setForm((corrente) => ({
                      ...corrente,
                      fornitore: valore,
                      fornitore_piva: anagrafica?.piva || "",
                    }));
                  }}
                  options={[...(form.fornitore&&!config.fornitori.includes(form.fornitore)?[{value:form.fornitore,label:form.fornitore}]:[]),...config.fornitori.map(fornitore=>({value:fornitore,label:fornitore}))]}
                  placeholder="Seleziona il fornitore" searchPlaceholder="Scrivi il fornitore..." />
              </div>

              <div className="sm:col-span-2">
                <Label>Azienda *</Label>

                <Select
                  value={
                    form.azienda_id ||
                    (form.azienda_nome
                      ? "__azienda_non_censita__"
                      : undefined)
                  }
                  onValueChange={(valore) => {
                    if (valore === "__azienda_non_censita__") return;
                    const azienda = aziende.find(
                      (item) => item.id === valore
                    );
                    setForm((corrente) => ({
                      ...corrente,
                      azienda_id: valore,
                      azienda_nome: azienda?.nome || "",
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona l'azienda" />
                  </SelectTrigger>

                  <SelectContent>
                    {!form.azienda_id && form.azienda_nome && (
                      <SelectItem value="__azienda_non_censita__">
                        {form.azienda_nome} (letta dal PDF)
                      </SelectItem>
                    )}

                    {aziende.map((azienda) => (
                      <SelectItem key={azienda.id} value={azienda.id}>
                        {azienda.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {form.azienda_nome && !form.azienda_id && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Azienda letta dalla fattura; puoi associarla a una voce dell'anagrafica.
                  </p>
                )}
              </div>

              <div className="sm:col-span-2 flex flex-wrap items-center gap-x-8 gap-y-3 rounded-lg border bg-muted/30 p-4">
                <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-primary"
                    checked={Boolean(form.ripartizione_attiva)}
                    onChange={(event) => {
                      const attiva = event.target.checked;
                      setForm((corrente) => {
                        const notePulite = pulisciNotaRipartizione(corrente.note);

                        return {
                          ...corrente,
                          ripartizione_attiva: attiva,
                          magazzino: attiva ? false : corrente.magazzino,
                          attrezzatura: attiva ? false : corrente.attrezzatura,
                          cantiere_id: attiva ? null : corrente.cantiere_id,
                          lavorazione_id: attiva ? null : corrente.lavorazione_id,
                          note: notePulite,
                          materiale_descrizione: attiva && /^\s*(?:magazzino|attrezzatura|magazzino\s*\/\s*attrezzatura)\s*$/i.test(corrente.materiale_descrizione || "") ? "" : corrente.materiale_descrizione,
                          ripartizioni: attiva && corrente.ripartizioni.length < 2
                            ? [
                                { cantiere_id: "", lavorazione_id: null, importo: "", magazzino: false, attrezzatura: false, attrezzatura_nome: "" },
                                { cantiere_id: "", lavorazione_id: null, importo: String(Number(corrente.imponibile) || 0), magazzino: false, attrezzatura: false, attrezzatura_nome: "" },
                              ]
                            : corrente.ripartizioni,
                        };
                      });
                    }}
                  />
                  Ripartisci tra più cantieri
                </label>
                {!form.ripartizione_attiva && (
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary"
                      checked={Boolean(form.magazzino)}
                      onChange={(event) => setForm((corrente) => ({
                        ...corrente,
                      magazzino: event.target.checked,
                      attrezzatura: event.target.checked ? false : corrente.attrezzatura,
                      cantiere_id: event.target.checked ? null : corrente.cantiere_id,
                      lavorazione_id: event.target.checked ? null : corrente.lavorazione_id,
                      materiale_descrizione: event.target.checked
                        ? "Magazzino"
                        : (/^\s*magazzino\s*$/i.test(corrente.materiale_descrizione || "") ? "" : corrente.materiale_descrizione),
                    }))}
                    />
                    Magazzino
                  </label>
                )}
                {!form.ripartizione_attiva && (
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary"
                      checked={Boolean(form.attrezzatura)}
                      onChange={(event) => setForm((corrente) => ({
                        ...corrente,
                        attrezzatura: event.target.checked,
                        magazzino: event.target.checked ? false : corrente.magazzino,
                        cantiere_id: event.target.checked ? null : corrente.cantiere_id,
                        lavorazione_id: event.target.checked ? null : corrente.lavorazione_id,
                        materiale_descrizione: event.target.checked
                          ? "Attrezzatura"
                          : (/^\s*attrezzatura\s*$/i.test(corrente.materiale_descrizione || "") ? "" : corrente.materiale_descrizione),
                      }))}
                    />
                    Attrezzatura
                  </label>
                )}
              </div>

              {!form.ripartizione_attiva && <>
              <div className="sm:col-span-2">
                <Label>Cantiere associato (facoltativo)</Label>

                <SearchableCombobox
                  value={form.magazzino || form.attrezzatura ? "" : (form.cantiere_id || "__nessuno__")}
                  onValueChange={(valore) =>
                    {
                      setLavorazioneFatturaCreataId(null);
                      setForm((corrente) => ({
                        ...corrente,
                        cantiere_id:
                          valore === "__nessuno__" ? null : valore,
                        lavorazione_id: null,
                      }));
                    }
                  }
                  options={[{value:"__nessuno__",label:"Nessun cantiere associato"},...cantieri.map(cantiere=>({value:cantiere.id,label:`${cantiere.tipo_destinazione==="collaborazione"?"Collaborazione · ":""}${cantiere.nome}`}))]}
                  placeholder={form.magazzino || form.attrezzatura ? "Non richiesto" : "Nessun cantiere associato"}
                  searchPlaceholder="Scrivi il cantiere..."
                  disabled={Boolean(form.magazzino || form.attrezzatura)} />

                <p className="mt-1 text-xs text-muted-foreground">
                  Se selezionato, l'imponibile sarà aggiunto ai
                  materiali del cantiere.
                </p>
              </div>
              {!form.magazzino && !form.attrezzatura && form.cantiere_id && !form.cantiere_id.startsWith("collab:") && (
                <div className="sm:col-span-2">
                  <Label>Lavorazione associata (facoltativa)</Label>
                  <Select
                    value={form.lavorazione_id || "__nessuna__"}
                    onValueChange={(valore) =>
                      set(
                        "lavorazione_id",
                        valore === "__nessuna__" ? null : valore
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Nessuna lavorazione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__nessuna__">
                        Nessuna lavorazione
                      </SelectItem>
                      {lavorazioniFattura.map((lavorazione) => (
                        <SelectItem
                          key={lavorazione.id}
                          value={lavorazione.id}
                        >
                          {lavorazione.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <NuovaLavorazioneButton
                    cantiereId={form.cantiere_id}
                    onCreated={nuovaLavorazioneFattura}
                    className="mt-2 w-full"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Il materiale creato dalla fattura sarà attribuito alla lavorazione selezionata.
                  </p>
                </div>
              )}
              {form.attrezzatura && (
                <div className="sm:col-span-2">
                  <Label>Nome attrezzatura</Label>
                  <Input value={form.attrezzatura_nome || ""} onChange={(event) => set("attrezzatura_nome", event.target.value)} placeholder="Es. Trapano, smerigliatrice, scala telescopica" />
                  <p className="mt-1 text-xs text-muted-foreground">Se non compilato sarà indicato il numero della fattura.</p>
                </div>
              )}
              </>}

              {form.ripartizione_attiva && (
                <div className="sm:col-span-2 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <Label>Quote per cantiere *</Label>
                    <Button type="button" variant="outline" size="sm" onClick={aggiungiQuota}>
                      <Plus className="h-4 w-4 mr-2" /> Aggiungi quota
                    </Button>
                  </div>

                  {(form.ripartizioni || []).map((quota, indice) => (
                    <div key={indice} className="rounded-lg border p-3 space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-sm">Quota {indice + 1}</p>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="text-destructive"
                          onClick={() => eliminaQuota(indice)}
                          disabled={form.ripartizioni.length <= 2}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <Label>Cantiere *</Label>
                          <SearchableCombobox
                            value={quota.cantiere_id || undefined}
                            onValueChange={(valore) => {
                              aggiornaQuota(indice, { cantiere_id: valore, lavorazione_id: null });
                              caricaLavorazioniQuota(valore);
                            }}
                            options={cantieri.map(cantiere=>({value:cantiere.id,label:`${cantiere.tipo_destinazione==="collaborazione"?"Collaborazione · ":""}${cantiere.nome}`}))}
                            placeholder={quota.magazzino || quota.attrezzatura ? "Non richiesto" : "Seleziona cantiere"}
                            searchPlaceholder="Scrivi il cantiere..."
                            disabled={Boolean(quota.magazzino || quota.attrezzatura)} />
                          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
                            <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold">
                              <input
                                type="checkbox"
                                className="h-4 w-4 accent-primary"
                                checked={Boolean(quota.magazzino)}
                                onChange={(event) => {
                                  const selezionata = event.target.checked;
                                  setForm((corrente) => {
                                    const ripartizioni = corrente.ripartizioni.map((voce, posizione) =>
                                      posizione === indice
                                        ? {
                                            ...voce,
                                            magazzino: selezionata,
                                            attrezzatura: selezionata ? false : voce.attrezzatura,
                                            cantiere_id: selezionata ? "" : voce.cantiere_id,
                                            lavorazione_id: selezionata ? null : voce.lavorazione_id,
                                          }
                                        : voce
                                    );
                                    return {
                                      ...corrente,
                                      ripartizioni,
                                      materiale_descrizione: descrizioneDestinazioni(ripartizioni, corrente.materiale_descrizione),
                                    };
                                  });
                                }}
                              />
                              Magazzino
                            </label>
                            <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold">
                              <input
                                type="checkbox"
                                className="h-4 w-4 accent-primary"
                                checked={Boolean(quota.attrezzatura)}
                                onChange={(event) => {
                                  const selezionata = event.target.checked;
                                  setForm((corrente) => {
                                    const ripartizioni = corrente.ripartizioni.map((voce, posizione) =>
                                      posizione === indice ? {
                                        ...voce,
                                        attrezzatura: selezionata,
                                        magazzino: selezionata ? false : voce.magazzino,
                                        cantiere_id: selezionata ? "" : voce.cantiere_id,
                                        lavorazione_id: selezionata ? null : voce.lavorazione_id,
                                      } : voce
                                    );
                                    return {
                                      ...corrente,
                                      ripartizioni,
                                      materiale_descrizione: descrizioneDestinazioni(ripartizioni, corrente.materiale_descrizione),
                                    };
                                  });
                                }}
                              />
                              Attrezzatura
                            </label>
                          </div>
                        </div>
                        <div>
                          <Label>Lavorazione (facoltativa)</Label>
                          <Select
                            value={quota.lavorazione_id || "__nessuna__"}
                            onValueChange={(valore) =>
                              aggiornaQuota(indice, {
                                lavorazione_id: valore === "__nessuna__" ? null : valore,
                              })
                            }
                            disabled={quota.magazzino || quota.attrezzatura || !quota.cantiere_id || quota.cantiere_id.startsWith("collab:")}
                          >
                            <SelectTrigger><SelectValue placeholder="Nessuna lavorazione" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__nessuna__">Nessuna lavorazione</SelectItem>
                              {(lavorazioniQuote[quota.cantiere_id] || []).map((lavorazione) => (
                                <SelectItem key={lavorazione.id} value={lavorazione.id}>
                                  {lavorazione.nome}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <NuovaLavorazioneButton
                            cantiereId={quota.cantiere_id && !quota.cantiere_id.startsWith("collab:") ? quota.cantiere_id : ""}
                            onCreated={(nuova) => nuovaLavorazioneQuota(indice, quota.cantiere_id, nuova)}
                            className="mt-2 w-full"
                          />
                        </div>
                        <div>
                          <Label>Imponibile quota (€) *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={quota.importo}
                            onChange={(event) => aggiornaImportoQuota(indice, event.target.value)}
                            readOnly={indice > 0 && indice === form.ripartizioni.length - 1}
                            className={indice > 0 && indice === form.ripartizioni.length - 1 ? "bg-muted" : undefined}
                            required
                          />
                          {indice > 0 && indice === form.ripartizioni.length - 1 && (
                            <p className="mt-1 text-xs font-medium text-primary">Residuo calcolato automaticamente</p>
                          )}
                        </div>
                      </div>
                      {quota.attrezzatura && <div><Label>Nome attrezzatura</Label><Input value={quota.attrezzatura_nome || ""} onChange={(event) => aggiornaQuota(indice, { attrezzatura_nome: event.target.value })} placeholder="Es. Trapano, smerigliatrice, scala telescopica" /></div>}
                    </div>
                  ))}

                  <div className={`rounded-lg border p-3 ${ripartizioneCorretta ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                      <span>Imponibile fattura: <strong>{eur(Number(form.imponibile) || 0)}</strong></span>
                      <span>Quote: <strong>{eur(totaleRipartito)}</strong></span>
                      <span className={ripartizioneCorretta ? "text-emerald-700" : "text-red-700"}>
                        {ripartizioneCorretta
                          ? "Ripartizione corretta"
                          : `Differenza: ${eur(differenzaRipartizione)}`}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              <div className="sm:col-span-2">
                <Label>Descrizione fattura</Label>
                <Input
                  value={form.materiale_descrizione || ""}
                  onChange={(event) =>
                    set(
                      "materiale_descrizione",
                      event.target.value
                    )
                  }
                  placeholder="Descrizione della fornitura o del servizio"
                />
              </div>

            </div>

            <div className="rounded-lg bg-muted p-4 grid grid-cols-2 gap-4">
              <div>
                <span className="text-muted-foreground">
                  Importo IVA
                </span>
                <strong className="block text-lg">
                  {eur(ivaImporto)}
                </strong>
              </div>

              <div>
                <span className="text-muted-foreground">
                  Totale fattura
                </span>
                <strong className="block text-lg text-primary">
                  {eur(totale)}
                </strong>
              </div>
            </div>

            <div>
              <Label>Note</Label>
              <Textarea
                value={form.note}
                onChange={(event) =>
                  set("note", event.target.value)
                }
                rows={3}
              />
            </div>

            <div>
              <Label>Fattura / allegato</Label>
              <Input
                type="file"
                accept="image/*,.pdf"
                onChange={(event) =>
                  setFile(event.target.files?.[0] || null)
                }
              />

              {(file || form.allegato_nome) && (
                <p className="text-sm text-muted-foreground mt-1">
                  <Paperclip className="inline h-4 w-4 mr-1" />
                  {file?.name || form.allegato_nome}
                </p>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
              >
                Annulla
              </Button>

              <Button
                type="submit"
                disabled={saving}
              >
                {saving ? "Salvataggio..." : "Salva"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={fornitoreOpen} onOpenChange={setFornitoreOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuovo fornitore dalla fattura</DialogTitle>
            <DialogDescription>
              Il fornitore non è ancora censito. Verifica i dati letti dal PDF e premi Salva fornitore.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={salvaFornitoreImportato} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><Label>Ragione sociale *</Label><Input value={nuovoFornitore.nome} onChange={(e) => setNuovoFornitore((v) => ({ ...v, nome: e.target.value }))} required /></div>
              <div><Label>Partita IVA *</Label><Input value={nuovoFornitore.piva} onChange={(e) => setNuovoFornitore((v) => ({ ...v, piva: e.target.value }))} required /></div>
              <div><Label>Codice fiscale</Label><Input value={nuovoFornitore.codice_fiscale} onChange={(e) => setNuovoFornitore((v) => ({ ...v, codice_fiscale: e.target.value }))} /></div>
              <div><Label>Email</Label><Input type="email" value={nuovoFornitore.email} onChange={(e) => setNuovoFornitore((v) => ({ ...v, email: e.target.value }))} /></div>
              <div><Label>Telefono</Label><Input value={nuovoFornitore.telefono} onChange={(e) => setNuovoFornitore((v) => ({ ...v, telefono: e.target.value }))} /></div>
              <div className="sm:col-span-2"><Label>Indirizzo</Label><Input value={nuovoFornitore.indirizzo} onChange={(e) => setNuovoFornitore((v) => ({ ...v, indirizzo: e.target.value }))} /></div>
              <div className="sm:col-span-2"><Label>Note</Label><Textarea value={nuovoFornitore.note} onChange={(e) => setNuovoFornitore((v) => ({ ...v, note: e.target.value }))} /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFornitoreOpen(false)}>Annulla</Button>
              <Button type="submit" disabled={savingFornitore}>{savingFornitore ? "Salvataggio..." : "Salva fornitore"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
